import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import "./AdminHome.css";
import * as XLSX from "xlsx";
import {
  FaShieldAlt,
  FaTrash,
  FaSpinner,
  FaFilter,
  FaEdit,
  FaSave,
  FaTimes,
  FaBars,
} from "react-icons/fa";
import { Link } from "react-router-dom";
// import Message from "./Message";
import BranchDetailLedger from "./BranchDetailLedger.jsx"; // Ensure the path is correct
const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;
const R_URI = process.env.REACT_APP_AUTH_DOMAIN;
const SUMMART_API = `https://${process.env.REACT_APP_API_KEY_SUMMARY}.execute-api.ap-south-1.amazonaws.com/default/getAllBranchSummary`;
const VIEW_REQUEST_API = `https://${process.env.REACT_APP_API_KEY_VIEW_REQUEST}.execute-api.ap-south-1.amazonaws.com/default/branchViewRequest`;
const BRANCH_API_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BRANCH}.execute-api.ap-south-1.amazonaws.com/default/createBranch`;
const PARTY_API_URL = `https://${process.env.REACT_APP_THIRD_PARTY_API_URL}.execute-api.ap-south-1.amazonaws.com/default/thirdPartyUpdate`;

const EMPTY_BRANCH = { opBal: 0, totalIn: 0, btIn: 0, totalOut: 0, btOut: 0, comm: 0, exp: 0, closingBal: 0 };

export default function AdminHome() {
  const [transactions, setTransactions] = useState([]);
  const [allTPTransactions, setAllTPTransactions] = useState([]);
  const [branchSummary, setBranchSummary] = useState({});
  const [allBranchNames, setAllBranchNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  // const [dbBalances, setDbBalances] = useState({});
  const auth = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const tableSearchTimeoutRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const isHoveringTableRef = useRef(false);
  const branchRowRefs = useRef({});
  const tableSearchRef = useRef("");
  const matchedBranchRef = useRef(null);
  const todayStr = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().split("T")[0];

  // Summary Date Filter
  const [summaryStart, setSummaryStart] = useState(todayStr);
  const [summaryEnd, setSummaryEnd] = useState(todayStr);

  // Table Date Filter
  const [tableStart] = useState("");
  const [tableEnd] = useState("");
  const [viewRequests, setViewRequests] = useState([]);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const fetchAllTransactions = useCallback(
    async (s, e) => {
      if (!auth.user?.access_token) return;

      setLoading(true);
      try {
        // 2. Construct the URL with Query Parameters
        // This matches the Lambda's event.queryStringParameters requirement
        const baseUrl = SUMMART_API;
        const url = `${baseUrl}?start=${s}&end=${e}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            "Content-Type": "application/json",
          },
          // NO 'body' property here! GET requests use the URL above.
        });

        // 3. Handle Errors
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(
            errorData.error || `HTTP error! status: ${res.status}`,
          );
        }

        const result = await res.json();

        // Set both states from the one response
        setBranchSummary(result.summary);
        setTransactions(result.transactions);

        // 4. Update State
      } catch (err) {
        console.error("Failed to fetch branch summary:", err.message);
        // Optional: Add a toast or message alert for the user here
      } finally {
        setLoading(false);
      }
    },
    [auth.user?.access_token],
  );

  useEffect(() => {
    fetchAllTransactions(todayStr, todayStr);
  }, [fetchAllTransactions, todayStr]);

  useEffect(() => {
    fetch(BRANCH_API_URL)
      .then((r) => r.json())
      .then((data) => {
        const names = (data.branches || [])
          .map((b) => b.branchName)
          .filter(Boolean)
          .sort();
        setAllBranchNames(names);
      })
      .catch(() => {});
  }, []);

  const fetchViewRequests = useCallback(async () => {
    try {
      const res = await fetch(`${VIEW_REQUEST_API}?admin=true`);
      const data = await res.json();
      setViewRequests(data.requests || []);
    } catch (err) {
      console.error("View requests fetch error:", err);
    }
  }, []);

  useEffect(() => { fetchViewRequests(); }, [fetchViewRequests]);

  // Fetch ALL TP transactions once on mount for accurate historical commission
  useEffect(() => {
    fetch(API_URL)
      .then((r) => r.json())
      .then((data) => {
        const tp = (data.transactions || []).filter(
          (tx) => tx.thirdParty && tx.thirdParty !== "NONE" && tx.status !== "REJECTED"
        );
        setAllTPTransactions(tp);
      })
      .catch(() => {});
  }, []);

  // Table keyboard search — active while mouse is over the summary table
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isHoveringTableRef.current) return;
      if (e.key === "Escape") {
        tableSearchRef.current = "";
        setTableSearch("");
        return;
      }
      if (e.key === "Enter") {
        if (matchedBranchRef.current) {
          setSelectedBranch(matchedBranchRef.current);
          tableSearchRef.current = "";
          setTableSearch("");
          matchedBranchRef.current = null;
        }
        return;
      }
      if (e.key === "Backspace") {
        tableSearchRef.current = tableSearchRef.current.slice(0, -1);
      } else if (e.key.length === 1) {
        tableSearchRef.current = tableSearchRef.current + e.key.toUpperCase();
      } else {
        return;
      }
      setTableSearch(tableSearchRef.current);
      clearTimeout(tableSearchTimeoutRef.current);
      tableSearchTimeoutRef.current = setTimeout(() => {
        tableSearchRef.current = "";
        setTableSearch("");
      }, 1800);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Scroll to + highlight matching branch row as tableSearch changes
  useEffect(() => {
    if (!tableSearch) { matchedBranchRef.current = null; return; }
    const matchId = Object.keys(branchRowRefs.current).find(id =>
      id.toUpperCase().startsWith(tableSearch)
    );
    matchedBranchRef.current = matchId || null;
    if (matchId && branchRowRefs.current[matchId]) {
      branchRowRefs.current[matchId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [tableSearch]);

  const handleViewRequestAction = async (requestId, action, requestedDates, selectedDates) => {
    setRequestActionLoading(requestId + action);
    try {
      const res = await fetch(VIEW_REQUEST_API, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, action, grantedDates: selectedDates || requestedDates }),
      });
      if (!res.ok) throw new Error("Action failed");
      await fetchViewRequests();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setRequestActionLoading(null);
    }
  };

  // 1. Data for the Excel-style Branch Summary
  // const summaryFilteredData = useMemo(() => {
  //   return transactions.filter((tx) => {
  //     const txDate = new Date(tx.timestamp).toISOString().split("T")[0];
  //     return txDate >= summaryStart && txDate <= summaryEnd;
  //   });
  // }, [transactions, summaryStart, summaryEnd]);

  // 2. Logic to build the Branch Map (Updated for SPLIT)
  // const checkDate = (timestamp, offsetDays = 0) => {
  //   const txDate = new Date(timestamp).toISOString().split("T")[0];
  //   const targetDate = new Date();
  //   targetDate.setDate(targetDate.getDate() - offsetDays);
  //   const targetStr = targetDate.toISOString().split("T")[0];
  //   return txDate === targetStr;
  // };
  const TPledgerData = useMemo(() => {
    const startStr = summaryStart;
    const endStr = summaryEnd;

    let openingBalance = 0;
    let historicalCommTotal = 0;
    const inRows = [];
    const outRows = [];

    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

    // Use allTPTransactions (all-time) for history so switching date range keeps history intact
    allTPTransactions.forEach((tx) => {
      if (!tx.timestamp) return;
      const txDateStr = new Date(new Date(tx.timestamp).getTime() + IST_OFFSET_MS)
        .toISOString()
        .split("T")[0];

      const amount = Number(tx.amount || 0);
      const comm = Number(tx.tpCommission || 0);
      const isIn = tx.paymentType?.includes("IN");

      if (txDateStr < startStr) {
        openingBalance += isIn ? amount : -amount;
        openingBalance -= comm;
        historicalCommTotal += comm;
      } else if (txDateStr >= startStr && txDateStr <= endStr) {
        const rowData = {
          branch: tx.fromBranch,
          amount,
          comm,
          date: tx.timestamp,
          transactionId: tx.paymentId,
          paymentType: tx.paymentType,
        };
        if (isIn) inRows.push(rowData);
        else outRows.push(rowData);
      }
    });

    // Calculate Totals for the Current Period
    const totalInAmt = inRows.reduce((a, b) => a + b.amount, 0);
    const totalOutAmt = outRows.reduce((a, b) => a + b.amount, 0);
    const totalInComm = inRows.reduce((a, b) => a + b.comm, 0);
    const totalOutComm = outRows.reduce((a, b) => a + b.comm, 0);

    const currentCommTotal = totalInComm + totalOutComm;

    // All-time TP commission income (historical + current period)
    const allTimeCommTotal = historicalCommTotal + currentCommTotal;

    const finalBalance =
      openingBalance + totalInAmt - (totalOutAmt + currentCommTotal);

    return {
      inRows,
      outRows,
      openingBalance,
      totalInAmt,
      totalInComm,
      totalOutAmt,
      totalOutComm,
      currentCommTotal,
      historicalCommTotal,
      allTimeCommTotal,
      finalBalance,
    };
  }, [allTPTransactions, summaryStart, summaryEnd]);
  // Merge allBranchNames with branchSummary so every branch always shows a row
  const fullBranchSummary = useMemo(() => {
    const merged = { ...branchSummary };
    allBranchNames.forEach((name) => {
      if (!merged[name]) merged[name] = { ...EMPTY_BRANCH };
    });
    return merged;
  }, [branchSummary, allBranchNames]);

  // old code hiden for reference (the branchSummaryMap is now built in the backend Lambda and returned as part of the response, so we don't need to calculate it here in the frontend anymore. This ensures consistency and reduces the chance of bugs in the complex summary logic.)

  // const branchSummaryMap = useMemo(() => {
  //   const branchMap = {};

  //   transactions.forEach((tx) => {
  //     if (["REJECTED", "SPLIT"].includes(tx.status)) return;
  //     if (tx.status === "PENDING" && tx.flowRole === "RECEIVER") return;

  //     const txDate = tx.timestamp?.split("T")[0];
  //     const amount = Number(tx.amount || 0);
  //     const comm = Number(tx.commission || 0);

  //     const currentBranch =
  //       tx.flowRole === "SENDER" ? tx.fromBranch : tx.toBranch;
  //     if (!currentBranch || currentBranch === "ADMIN") return;

  //     // Helper to initialize a branch/party in the map
  //     const initBranch = (id) => {
  //       if (!branchMap[id]) {
  //         branchMap[id] = {
  //           opBal: 0,
  //           totalIn: 0,
  //           btIn: 0,
  //           totalOut: 0,
  //           btOut: 0,
  //           comm: 0,
  //           exp: 0,
  //           closingBal: 0,
  //         };
  //       }
  //     };

  //     initBranch(currentBranch);
  //     const b = branchMap[currentBranch];
  //     const tpName =
  //       tx.thirdParty && tx.thirdParty !== "NONE" ? tx.thirdParty : null;

  //     const isHistory = summaryStart && txDate < summaryStart;
  //     const isCurrent = txDate >= summaryStart && txDate <= summaryEnd;
  //     // --- 1. HISTORY LOGIC (Opening Balance) ---
  //     if (isHistory) {
  //       // Branch Principal Amount Logic
  //       if (tx.flowRole === "SENDER") {
  //         if (
  //           ["OUT", "SPLITOUT", "BTOUT", "EXP", "BTSPLITOUT"].includes(
  //             tx.paymentType,
  //           )
  //         )
  //           b.opBal -= amount;
  //         else if (
  //           ["IN", "BTIN", "SPLITIN", "BTSPLITIN"].includes(tx.paymentType)
  //         )
  //           b.opBal += amount;
  //       } else {
  //         if (["IN", "BTIN", "SPLITIN", "BTSPLITIN"].includes(tx.paymentType))
  //           b.opBal += amount;
  //         else if (
  //           ["OUT", "SPLITOUT", "BTOUT", "BTSPLITOUT"].includes(tx.paymentType)
  //         )
  //           b.opBal -= amount;
  //       }

  //       // --- HISTORY COMMISSION LOGIC (THE FIX) ---
  //       if (comm > 0) {
  //         // if (tpName) {
  //         //   // If it's a third party commission from the past,
  //         //   // it must be added to the Third Party's Opening Balance
  //         //   initBranch(tpName);
  //         //   branchMap[tpName].opBal += comm;
  //         // } else {
  //         // If it's a regular commission, add to the branch's Opening Balance
  //         b.opBal += comm;
  //       }
  //     }

  //     // --- 2. CURRENT LOGIC (Today's Table Columns) ---
  //     else if (isCurrent) {
  //       // Commission Logic
  //       if (comm > 0) {
  //         if (tpName) {
  //           initBranch(tpName);
  //           branchMap[tpName].comm += comm;
  //         } else {
  //           b.comm += comm;
  //         }
  //       }

  //       // Amount Logic
  //       if (tx.flowRole === "SENDER") {
  //         if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType))
  //           b.btOut += amount;
  //         else if (tx.paymentType === "EXP") b.exp += amount;
  //         else if (["OUT", "SPLITOUT"].includes(tx.paymentType))
  //           b.totalOut += amount;
  //         else if (
  //           ["IN", "BTIN", "SPLITIN", "BTSPLITIN"].includes(tx.paymentType)
  //         ) {
  //           if (["BTIN", "BTSPLITIN"].includes(tx.paymentType))
  //             b.btIn += amount;
  //           else b.totalIn += amount;
  //         }
  //       } else if (tx.flowRole === "RECEIVER") {
  //         if (["BTIN", "BTSPLITIN"].includes(tx.paymentType)) b.btIn += amount;
  //         else if (["IN", "SPLITIN"].includes(tx.paymentType))
  //           b.totalIn += amount;
  //         else if (
  //           ["OUT", "BTOUT", "SPLITOUT", "BTSPLITOUT"].includes(tx.paymentType)
  //         ) {
  //           if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType))
  //             b.btOut += amount;
  //           else b.totalOut += amount;
  //         }
  //       }
  //     }
  //   });

  //   // --- 3. FINAL CALCULATION ---
  //   Object.keys(branchMap).forEach((bId) => {
  //     const b = branchMap[bId];
  //     b.closingBal =
  //       b.opBal +
  //       (b.totalIn + b.btIn + b.comm) -
  //       (b.totalOut + b.btOut + b.exp);
  //   });

  //   return branchMap;
  // }, [transactions, summaryStart, summaryEnd]);

  // 2b. Diagnose Cash/BT mismatch: find transactions in the period whose
  // linked counterpart is missing or has a non-mirroring paymentType.
  const diffTransactions = useMemo(() => {
    const inRange = (tx) => {
      if (!tx.timestamp) return false;
      const d = tx.timestamp.split("T")[0];
      return (!summaryStart || d >= summaryStart) && (!summaryEnd || d <= summaryEnd);
    };
    const byId = {};
    transactions.forEach((tx) => {
      byId[tx.paymentId] = tx;
    });

    const expectedOpposite = (type) => {
      if (!type) return null;
      if (type.includes("BTSPLIT")) return type.includes("IN") ? "BTSPLITOUT" : "BTSPLITIN";
      if (type.includes("SPLIT")) return type.includes("IN") ? "SPLITOUT" : "SPLITIN";
      if (type.startsWith("BT")) return type.includes("IN") ? "BTOUT" : "BTIN";
      if (type === "IN" || type === "OUT") return type === "IN" ? "OUT" : "IN";
      return null;
    };

    const relevant = ["IN", "OUT", "BTIN", "BTOUT", "SPLITIN", "SPLITOUT", "BTSPLITIN", "BTSPLITOUT"];

    return transactions
      .filter((tx) => inRange(tx) && tx.status !== "REJECTED" && relevant.includes(tx.paymentType))
      .map((tx) => {
        const expected = expectedOpposite(tx.paymentType);
        const linked = tx.linkedId ? byId[tx.linkedId] : null;
        let issue = null;
        if (!tx.linkedId) {
          issue = "No linked transaction (orphan leg)";
        } else if (!linked) {
          issue = "Linked transaction not found in records";
        } else if (expected && linked.paymentType !== expected) {
          issue = `Linked type mismatch (expected ${expected}, got ${linked.paymentType})`;
        } else if (Number(linked?.amount) !== Number(tx.amount) && !tx.paymentType.includes("SPLIT")) {
          issue = `Amount mismatch with linked (${tx.amount} vs ${linked.amount})`;
        }
        return issue ? { ...tx, issue } : null;
      })
      .filter(Boolean);
  }, [transactions, summaryStart, summaryEnd]);

  // 3. Data for the Detailed Ledger Table
  const tableFilteredData = useMemo(() => {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    setCurrentPage(1);
    return transactions
      .filter((tx) => {
        // 1. HARD FILTERS (Always Hide these)
        if (["REJECTED", "SPLIT"].includes(tx.status)) return false;

        // 2. SEARCH FILTER
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          !searchTerm ||
          tx.fromBranch?.toLowerCase().includes(search) ||
          tx.toBranch?.toLowerCase().includes(search) ||
          tx.senderName?.toLowerCase().includes(search) ||
          tx.receiverName?.toLowerCase().includes(search) ||
          tx.paymentId?.toLowerCase().includes(search);

        // 3. TYPE FILTER (The fix for your buttons)
        const matchesType =
          typeFilter === "ALL" || tx.paymentType === typeFilter;

        // 4. DATE FILTER (With Safety Check)
        let matchesDate = true;
        if (tx.timestamp && tableStart && tableEnd) {
          const dateObj = new Date(tx.timestamp);
          if (!isNaN(dateObj.getTime())) {
            const txDate = new Date(dateObj.getTime() + IST_OFFSET_MS)
              .toISOString()
              .split("T")[0];
            matchesDate = txDate >= tableStart && txDate <= tableEnd;
          } else {
            matchesDate = false;
          }
        }

        // ONLY return true if ALL conditions are met
        return matchesSearch && matchesType && matchesDate;
      })
      .filter((tx) => {
        if (!tx.timestamp) return false;
        const txDate = new Date(new Date(tx.timestamp).getTime() + IST_OFFSET_MS)
          .toISOString()
          .split("T")[0];
        return txDate >= summaryStart && txDate <= summaryEnd;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [
    transactions,
    searchTerm,
    typeFilter,
    tableStart,
    tableEnd,
    summaryEnd,
    summaryStart,
  ]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return tableFilteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [tableFilteredData, currentPage]);

  const totalPages = Math.ceil(tableFilteredData.length / itemsPerPage);

  // --- Handlers ---
  const handleEditSave = async (tx) => {
    setLoading(true);
    const newAmount = Number(editValues.amount);
    if (isNaN(newAmount) || newAmount < 0) {
      alert("Please enter a valid amount");
      setLoading(false);
      return;
    }
    try {
      // 1. Prepare the payload (Make sure amount is a Number)
      const updatePayload = {
        paymentId: tx.paymentId,
        paymentType: editValues.paymentType || tx.paymentType,
        amount: Number(editValues.amount), // Ensure this is not a string
        commission: Number(editValues.commission), // Ensure this is not a string
        senderName: editValues.senderName,
        senderCell: editValues.senderCell,
        receiverName: editValues.receiverName,
        receiverCell: editValues.receiverCell,
        remarks: editValues.remarks,
        status: editValues.status || tx.status,
        fromBranch: editValues.fromBranch || tx.fromBranch,
        toBranch: editValues.toBranch || tx.toBranch,
        editingBranch: "ADMIN",
        linkedId: tx.linkedId || null,
      };

      // 2. Update the Ledger (DynamoDB)
      const response = await fetch(API_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) throw new Error("Database update failed");

      // 3. THE FIX: REBUILD THE BALANCES
      // Because the amount changed, the old balance in the database is now WRONG.
      // We run the Master Rebuild to calculate the new absolute truth.
      console.log("Recalculating balances after amount change...");

      alert("Update Successful: Transaction updated");
      setEditingId(null);
      await fetchAllTransactions(todayStr, todayStr);
    } catch (err) {
      console.error("Edit Failed:", err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteTransaction = async (tx) => {
    if (
      !window.confirm(
        "ARE YOU SURE? This will REJECT this record and ALL its related split/linked records, then recalculate balances for all affected branches.",
      )
    )
      return;

    setLoading(true);
    try {
      // Extract base transaction ID: "TX-1780734322811-IN" → "TX-1780734322811"
      // Payment IDs follow the pattern TX-{timestamp}-{TYPE}[-PART-{n}]
      // We match all records sharing the same timestamp base.
      const baseId = tx.paymentId.match(/^([A-Z]+-\d+)/)?.[1];

      // Find all related records (same base ID, not already rejected)
      const relatedTxs = baseId
        ? transactions.filter(
            (t) => t.paymentId.startsWith(baseId) && t.status !== "REJECTED",
          )
        : [tx];

      // PATCH every related record to REJECTED
      const results = await Promise.all(
        relatedTxs.map((t) =>
          fetch(API_URL, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentId: t.paymentId,
              paymentType: t.paymentType,
              status: "REJECTED",
              remarks: `Deleted by Admin (Group: ${baseId || tx.paymentId})`,
            }),
          }),
        ),
      );

      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0)
        throw new Error(
          `${failed.length} record(s) could not be rejected. Check console.`,
        );

      alert("Transactions voided and balances rebuilt successfully.");
      fetchAllTransactions(todayStr, todayStr);
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleSignOut = () => {
    auth.removeUser();
    localStorage.clear();
    const clientId = "1a39ivjo9rt6lqngrp085egiqp";
    const domain = "ap-south-1568k9cbda.auth.ap-south-1.amazoncognito.com";
    const cognitoLogoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      R_URI,
    )}`;
    window.location.href = cognitoLogoutUrl;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const exportToExcel = () => {
    // 1. Define the Worksheet and initial data
    const wb = XLSX.utils.book_new();

    // 2. Build the rows manually as an array of arrays (AOA)
    const rows = [
      ["ALL BRANCH SUMMARY STATEMENT"], // Row 1
      [`Period: ${summaryStart} to ${summaryEnd}. Time:${new Date()}`], // Row 2
      [], // Row 3 (Spacer)
      [
        "BRANCH NAME",
        "OP BAL",
        "TOTAL IN",
        "BT IN",
        "TOTAL OUT",
        "BT OUT",
        "COMM",
        "EXP",
        "CLOSING BALANCE",
      ], // Row 4 (Headers)
    ];

    // 3. Add Branch Data rows
    let grandTotals = {
      op: 0,
      tin: 0,
      btin: 0,
      tout: 0,
      btout: 0,
      com: 0,
      exp: 0,
      close: 0,
    };

    Object.keys(fullBranchSummary)
      .sort()
      .forEach((id) => {
        const b = fullBranchSummary[id];
        const closing =
          b.opBal +
          (b.totalIn + b.btIn) -
          (b.totalOut + b.btOut + b.exp) +
          b.comm;

        rows.push([
          id,
          b.opBal,
          b.totalIn,
          b.btIn,
          b.totalOut,
          b.btOut,
          b.comm,
          b.exp,
          closing,
        ]);

        // Accumulate Grand Totals
        grandTotals.op += b.opBal;
        grandTotals.tin += b.totalIn;
        grandTotals.btin += b.btIn;
        grandTotals.tout += b.totalOut;
        grandTotals.btout += b.btOut;
        grandTotals.com += b.comm;
        grandTotals.exp += b.exp;
        grandTotals.close += closing;
      });

    // 4. Add the Grand Total Row
    rows.push([
      "GRAND TOTAL",
      grandTotals.op + (TPledgerData.historicalCommTotal || 0),
      grandTotals.tin,
      grandTotals.btin,
      grandTotals.tout,
      grandTotals.btout,
      grandTotals.com + (TPledgerData.currentCommTotal || 0),
      grandTotals.exp,
      grandTotals.close + TPledgerData.historicalCommTotal + TPledgerData.currentCommTotal,
    ]);

    // 5. Add the Custom Final Reconciliation Row
    // const finalValue =
    //   grandTotals.close + TPledgerData.historicalCommTotal + TPledgerData.currentCommTotal;
    rows.push([
      "",
      "",
      "",
      "",
      "",
      "",
      `Com+Tp: ${grandTotals.com}+${TPledgerData.currentCommTotal}`,
      "",
      "",
    ]);
    // 6. Convert array of arrays to sheet
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // 7. Styling/Merging (Optional - only works if using xlsx-js-style or similar)
    // Merging the top header rows across all columns
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }, // Merge row 1
      { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } }, // Merge row 2
    ];

    XLSX.utils.book_append_sheet(wb, ws, "All Branch Summary");

    // 8. Download
    XLSX.writeFile(
      wb,
      `All_Branch_Summary_${summaryStart}_to_${summaryEnd}.xlsx`,
    );
  };

  return (
    <>
      {loading && (
        <>
          {/* Click-blocker: transparent, full-viewport, no visual change */}
          <div className="admin-click-guard" />
          {/* Top progress bar */}
          <div className="admin-top-loader">
            <div className="admin-top-loader-bar">
              <span className="admin-top-loader-glow" />
            </div>
            <div className="admin-top-loader-dots">
              <span /><span /><span />
            </div>
          </div>
        </>
      )}
    <div className="admin-container">
      <header className="admin-header">
        <h1 style={{ color: "white" }}>
          <FaShieldAlt /> Administrator
        </h1>
        <div className="menu-icon" onClick={toggleMenu}>
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </div>

        {/* The Menu Links */}
        <div className={`admin-header-buttons ${isMenuOpen ? "active" : ""}`}>
          <Link to="/bm" onClick={closeMenu}>
            <button className="Branch-nav">Branch View</button>
          </Link>
          <Link to="/admin-balance" onClick={closeMenu}>
            <button className="Branch-nav">Branch Balance</button>
          </Link>
          <Link to="/brm" onClick={closeMenu}>
            <button className="Branch-nav">Bankers View</button>
          </Link>
          <Link to="/bankers-ledger" onClick={closeMenu}>
            <button className="Branch-nav">Bankers Ledger</button>
          </Link>
          <Link to="/btb-banker-matching" onClick={closeMenu}>
            <button className="Branch-nav">BTB vs Banker</button>
          </Link>
          <Link to="/third-party-management" onClick={closeMenu}>
            <button className="Branch-nav">T-P View</button>
          </Link>
          <Link to="/third-party" onClick={closeMenu}>
            <button className="Branch-nav">T-P Ledger</button>
          </Link>
          {/* <Link to="/waiting" onClick={closeMenu}>
            <button className="Branch-nav">Waiting</button>
          </Link> */}
          <Link to="/admin-control" onClick={closeMenu}>
            <button className="Branch-nav">Status</button>
          </Link>
          <Link to="/admin-message-monitor" onClick={closeMenu}>
            <button className="Branch-nav">Messages</button>
          </Link>
          <Link to="/commission-audit" onClick={closeMenu}>
            <button className="Branch-nav">Commission Audit</button>
          </Link>
          {/* <Link to="/admin-self-manage" onClick={closeMenu}>
            <button className="Branch-nav">Self Approve</button>
          </Link> */}
          <Link to="/admin-statement" onClick={closeMenu}>
            <button className="Branch-nav">Statement</button>
          </Link>
          <Link to="/admin/video-control">
            <button className="Branch-nav">Demo</button>
          </Link>
          <Link to="/admin-entry" onClick={closeMenu}>
            <button className="Branch-nav">Admin Entry</button>
          </Link>
          <Link to="/admin-date-editor" onClick={closeMenu}>
            <button className="Branch-nav">Date Editor</button>
          </Link>
          <Link to="/admin-cleanup" onClick={closeMenu}>
            <button className="Branch-nav">Cleanup</button>
          </Link>
          <Link to="/admin-error-check" onClick={closeMenu}>
            <button className="Branch-nav">Error Check</button>
          </Link>
          {/* Action Buttons */}
          <button
            onClick={() => {
              setSummaryStart(todayStr);
              setSummaryEnd(todayStr);
              fetchAllTransactions(todayStr, todayStr);
              closeMenu();
            }}
            disabled={loading}
            className="Branch-nav"
          >
            <FaSpinner className={loading ? "spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => { setShowRequestsPanel((p) => !p); closeMenu(); }}
            className="Branch-nav"
            style={{ position: "relative" }}
          >
            View Requests
            {viewRequests.length > 0 && (
              <span style={{ background: "#ef4444", color: "#fff", borderRadius: "50%", fontSize: "0.65rem", fontWeight: 700, padding: "1px 5px", marginLeft: 4 }}>
                {viewRequests.length}
              </span>
            )}
          </button>
          <button onClick={handleSignOut} className="btn-signout">
            Sign Out
          </button>
        </div>
      </header>

      {/* View Requests Panel */}
      {showRequestsPanel && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, margin: "12px 16px", padding: "16px 20px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: "#1e3a5f", fontSize: "0.9rem", fontWeight: 700 }}>Branch History View Requests</h3>
            <button onClick={fetchViewRequests} style={{ background: "none", border: "1px solid #cbd5e1", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: "0.75rem", color: "#475569" }}>↻ Refresh</button>
          </div>
          {viewRequests.length === 0 ? (
            <p style={{ color: "#94a3b8", fontSize: "0.82rem", margin: 0 }}>No pending requests.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th style={{ padding: "7px 10px", textAlign: "left", color: "#475569", fontWeight: 700 }}>Branch</th>
                  <th style={{ padding: "7px 10px", textAlign: "left", color: "#475569", fontWeight: 700 }}>Requested Dates</th>
                  <th style={{ padding: "7px 10px", textAlign: "left", color: "#475569", fontWeight: 700 }}>Requested At</th>
                  <th style={{ padding: "7px 10px", textAlign: "center", color: "#475569", fontWeight: 700 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {viewRequests.map((req) => (
                  <tr key={req.requestId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1e3a5f" }}>{req.branchId}</td>
                    <td style={{ padding: "8px 10px", color: "#334155" }}>
                      {(req.requestedDates || []).map((d) => (
                        <span key={d} style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 4, padding: "1px 6px", marginRight: 4, fontSize: "0.75rem" }}>{d}</span>
                      ))}
                    </td>
                    <td style={{ padding: "8px 10px", color: "#64748b" }}>
                      {new Date(req.requestedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={{ padding: "8px 10px", textAlign: "center", display: "flex", gap: 8, justifyContent: "center" }}>
                      <button
                        onClick={() => handleViewRequestAction(req.requestId, "APPROVED", req.requestedDates)}
                        disabled={requestActionLoading === req.requestId + "APPROVED"}
                        style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem" }}
                      >
                        {requestActionLoading === req.requestId + "APPROVED" ? "…" : "Approve All"}
                      </button>
                      <button
                        onClick={() => handleViewRequestAction(req.requestId, "REJECTED")}
                        disabled={requestActionLoading === req.requestId + "REJECTED"}
                        style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontWeight: 600, fontSize: "0.78rem" }}
                      >
                        {requestActionLoading === req.requestId + "REJECTED" ? "…" : "Reject"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* --- SECTION 1: BRANCH MOVEMENT SUMMARY (EXCEL STYLE) --- */}
      <section className="admin-section movement-summary">
        <div className="admin-date-filters">
          <div className="date-group">
            <strong>Summary Period:</strong>

            <div className="date-controls">
              <label htmlFor="FromDate">
                From
                <input
                  type="date"
                  className="date-input"
                  value={summaryStart}
                  onChange={(e) => setSummaryStart(e.target.value)}
                />
              </label>

              <label htmlFor="ToDate">
                To
                <input
                  type="date"
                  className="date-input"
                  value={summaryEnd}
                  onChange={(e) => setSummaryEnd(e.target.value)}
                />
              </label>
              <button
                className="fetch-btn"
                onClick={() => fetchAllTransactions(summaryStart, summaryEnd)}
                disabled={loading}
                style={{
                  padding: "8px",
                  backgroundColor: "#f2ff00",
                  color: "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {loading ? "Loading..." : "Get Data"}
              </button>
            </div>

            <button
              className="btn-today"
              onClick={() => {
                setSummaryStart(todayStr);
                setSummaryEnd(todayStr);
                fetchAllTransactions(todayStr, todayStr);
              }}
            >
              Today
            </button>
            <button onClick={exportToExcel} className="excel-download-btn">
              Download Excel
            </button>
          </div>
        </div>

        <div
          className="table-responsive"
          ref={tableWrapperRef}
          onMouseEnter={() => { isHoveringTableRef.current = true; }}
          onMouseLeave={() => { isHoveringTableRef.current = false; }}
          style={{ position: "relative" }}
        >
          {tableSearch && (
            <div className="table-search-overlay">
              <span className="table-search-label">Jump to:</span>
              <span className="table-search-query">{tableSearch}</span>
            </div>
          )}
          <table className="movement-summary-table">
            <thead>
              <tr>
                <th>BRANCH NAME</th>
                <th>OP BAL</th>
                <th>TOTAL IN</th>
                <th>BT IN</th>
                <th>TOTAL OUT</th>
                <th>BT OUT</th>
                <th>COMM</th>
                <th>EXP</th>
                <th>CLOSING BALANCE</th>
                <th>BRANCH WISE</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(fullBranchSummary)
                .sort()
                .map((branchId) => {
                  const b = fullBranchSummary[branchId];
                  const closing =
                    b.opBal +
                    (b.totalIn + b.btIn) -
                    (b.totalOut + b.btOut + b.exp) +
                    b.comm;
                  const isMatch = tableSearch && branchId.toUpperCase().startsWith(tableSearch);
                  return (
                    <tr
                      key={branchId}
                      ref={el => { branchRowRefs.current[branchId] = el; }}
                      style={isMatch ? { background: "#fef9c3", outline: "2px solid #f59e0b" } : undefined}
                    >
                      <td>
                        <strong>{branchId}</strong>
                      </td>
                      <td>{b.opBal.toLocaleString("en-IN")}</td>
                      <td className="txt-in">
                        {b.totalIn.toLocaleString("en-IN")}
                      </td>
                      <td className="txt-in">
                        {b.btIn.toLocaleString("en-IN")}
                      </td>
                      <td className="txt-out">
                        {b.totalOut.toLocaleString("en-IN")}
                      </td>
                      <td className="txt-bt-out">
                        {b.btOut.toLocaleString("en-IN")}
                      </td>
                      <td className="txt-in">
                        {b.comm.toLocaleString("en-IN")}
                      </td>
                      <td className="txt-exp">
                        {b.exp.toLocaleString("en-IN")}
                      </td>
                      <td className={closing < 0 ? "neg-bal" : "pos-bal"}>
                        {closing.toLocaleString("en-IN")}
                      </td>
                      <td>
                        <button
                          onClick={() => setSelectedBranch(branchId)}
                          className="Branch-nav"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              {(() => {
                const totals = Object.values(fullBranchSummary).reduce(
                  (acc, b) => {
                    const closing =
                      b.opBal +
                      (b.totalIn + b.btIn) -
                      (b.totalOut + b.btOut + b.exp) +
                      b.comm;
                    acc.opBal += b.opBal;
                    acc.totalIn += b.totalIn;
                    acc.btIn += b.btIn;
                    acc.totalOut += b.totalOut;
                    acc.btOut += b.btOut;
                    acc.comm += b.comm;
                    acc.exp += b.exp;
                    acc.closing += closing;
                    return acc;
                  },
                  {
                    opBal: 0,
                    totalIn: 0,
                    btIn: 0,
                    totalOut: 0,
                    btOut: 0,
                    comm: 0,
                    exp: 0,
                    closing: 0,
                  },
                );

                const cashDiff = totals.totalIn - totals.totalOut;
                const btDiff = totals.btIn - totals.btOut;
                const diffColor = (v) => ({ color: v === 0 ? "#16a34a" : v > 0 ? "#1d4ed8" : "#dc2626", fontWeight: "bold" });
                return (
                  <>
                  <tr
                    className="total-row"
                    style={{
                      fontWeight: "bold",
                      borderTop: "2px solid #ccc",
                      backgroundColor: "#fff200ff",
                    }}
                  >
                    <td>GRAND TOTAL</td>
                    <td>
                      <b>{(totals.opBal + (TPledgerData.historicalCommTotal || 0)).toLocaleString("en-IN")}</b>
                    </td>
                    <td className="txt-in">
                      <b>{totals.totalIn.toLocaleString("en-IN")}</b>
                    </td>
                    <td className="txt-bt">
                      <b>{totals.btIn.toLocaleString("en-IN")}</b>
                    </td>
                    <td className="txt-out">
                      <b>{totals.totalOut.toLocaleString("en-IN")}</b>
                    </td>
                    <td className="txt-bt">
                      <b>{totals.btOut.toLocaleString("en-IN")}</b>
                    </td>
                    <td>
                      <b>
                        {(
                          totals.comm + (TPledgerData.currentCommTotal || 0)
                        ).toLocaleString("en-IN")}
                        <br />
                        <small>
                          ({totals.comm} + {TPledgerData.currentCommTotal || 0})
                        </small>
                      </b>
                    </td>
                    <td className="txt-exp">
                      <b>{totals.exp.toLocaleString("en-IN")}</b>
                    </td>
                    <td className={totals.closing < 0 ? "neg-bal" : "pos-bal"}>
                      {(totals.closing + (TPledgerData.historicalCommTotal || 0) + (TPledgerData.currentCommTotal || 0)).toLocaleString("en-IN")}
                    </td>
                    <td>
                      <span>
                        Final:
                        {(totals.closing + (TPledgerData.historicalCommTotal || 0) + (TPledgerData.currentCommTotal || 0)).toLocaleString("en-IN")}
                      </span>
                      <br />(incl. today&apos;s TP-Com:{" "}
                      {(TPledgerData.currentCommTotal || 0).toLocaleString("en-IN")})
                      <br />
                      LAK Final:{" "}
                      {fullBranchSummary["LAK"]
                        ? (
                            fullBranchSummary["LAK"].closingBal -
                              TPledgerData.finalBalance || 0
                          ).toLocaleString("en-IN")
                        : "0"}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: "#f0fdf4", borderTop: "2px solid #16a34a", fontSize: "0.78rem" }}>
                    <td colSpan="2" style={{ fontWeight: 700, color: "#1e3a5f", paddingLeft: 10 }}>DIFFERENCE CHECK</td>
                    <td colSpan="2" style={diffColor(cashDiff)}>
                      Cash: IN−OUT = {cashDiff.toLocaleString("en-IN")}
                    </td>
                    <td colSpan="2" style={diffColor(btDiff)}>
                      BT: BTIN−BTOUT = {btDiff.toLocaleString("en-IN")}
                    </td>
                    <td colSpan="4" style={{ color: "#64748b", fontSize: "0.72rem" }}>
                      {cashDiff === 0 && btDiff === 0 ? (
                        "✓ Balanced"
                      ) : (
                        <span>
                          ⚠ Mismatch detected
                          <button
                            onClick={() => setShowDiffModal(true)}
                            style={{
                              marginLeft: 8,
                              background: "#dc2626",
                              color: "#fff",
                              border: "none",
                              borderRadius: 4,
                              padding: "2px 8px",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Investigate ({diffTransactions.length})
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                  </>
                );
              })()}

            </tfoot>
          </table>
        </div>
        {selectedBranch && (
          <div className="modal">
            <BranchDetailLedger
              branchId={selectedBranch}
              transactions={transactions}
              closingBalInfo={fullBranchSummary[selectedBranch]?.closingBal ?? 0}
              opBalance={fullBranchSummary[selectedBranch]?.opBal ?? 0}
              totalIn={fullBranchSummary[selectedBranch]?.totalIn ?? 0}
              totalOut={fullBranchSummary[selectedBranch]?.totalOut ?? 0}
              btIn={fullBranchSummary[selectedBranch]?.btIn ?? 0}
              btOut={fullBranchSummary[selectedBranch]?.btOut ?? 0}
              exp={fullBranchSummary[selectedBranch]?.exp ?? 0}
              comm={fullBranchSummary[selectedBranch]?.comm ?? 0}
              summaryStart={summaryStart}
              summaryEnd={summaryEnd}
              apiUrl={API_URL}
              onSave={() => fetchAllTransactions(summaryStart, summaryEnd)}
              onClose={() => setSelectedBranch(null)}
              allBranches={Object.keys(fullBranchSummary)}
              partyApiUrl={PARTY_API_URL}
            />
          </div>
        )}
        {showDiffModal && (
          <div className="modal">
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 20,
                maxWidth: "90vw",
                maxHeight: "85vh",
                overflow: "auto",
                margin: "5vh auto",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>
                  Mismatch Investigation ({summaryStart} to {summaryEnd})
                </h3>
                <button
                  onClick={() => setShowDiffModal(false)}
                  style={{ background: "#f1f5f9", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}
                >
                  Close
                </button>
              </div>
              <p style={{ color: "#64748b", fontSize: "0.85rem" }}>
                These transactions have a missing, unmatched, or mismatched linked record — the
                most common cause of a cash/BT imbalance. Found {diffTransactions.length} flagged
                record(s).
              </p>
              {diffTransactions.length === 0 ? (
                <p>No orphan/mismatched legs found in this date range. The mismatch may originate
                  from a transaction outside this period, or from a manual balance edit.</p>
              ) : (
                <table className="branch-table" style={{ width: "100%", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "#fee2e2" }}>
                      <th>Branch</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Payment ID</th>
                      <th>Linked ID</th>
                      <th>Status</th>
                      <th>Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffTransactions.map((tx) => (
                      <tr key={tx.paymentId}>
                        <td>{tx.fromBranch}</td>
                        <td>{tx.paymentType}</td>
                        <td>₹{Number(tx.amount).toLocaleString("en-IN")}</td>
                        <td>{tx.paymentId}</td>
                        <td>{tx.linkedId || "—"}</td>
                        <td>{tx.status}</td>
                        <td style={{ color: "#dc2626", fontWeight: 600 }}>{tx.issue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </section>

      {/* --- SECTION 2: DETAILED LEDGER --- */}
      <main className="admin-content">
        <h3 className="section-title">Detailed Ledger</h3>
        <div className="admin-filter-tabs">
          <div className="filter-left">
            <FaFilter />
            {[
              "ALL",
              "IN",
              "OUT",
              "BTIN",
              "BTOUT",
              "EXP",
              "SPLITOUT",
              "SPLITIN",
              "BTSPLITOUT",
              "BTSPLITIN",
            ].map((type) => (
              <button
                key={type}
                className={typeFilter === type ? "active" : ""}
                onClick={() => setTypeFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>
          <input
            className="admin-search-input"
            placeholder="Search Branch/Party/ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="table-responsive">
          <table className="branch-table">
            <thead>
              <tr>
                <th>TYPE</th>
                <th>FROM</th>
                <th>TO</th>

                <th>RECEIVER</th>
                <th>STATUS</th>
                <th>AMOUNT</th>
                <th>COMM</th>
                <th>ACTIONS</th>
                <th>DATE</th>
                <th>Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((tx) => {
                const isEditing = editingId === tx.paymentId;

                // Helper to update editValues state
                const handleInputChange = (field, value) => {
                  setEditValues((prev) => ({
                    ...prev,
                    [field]: value,
                  }));
                };

                return (
                  <tr
                    key={tx.paymentId}
                    className={isEditing ? "row-editing" : ""}
                  >
                    {/* 1. Payment Type Edit */}
                    <td>
                      {isEditing ? (
                        <select
                          value={editValues.paymentType}
                          onChange={(e) =>
                            handleInputChange("paymentType", e.target.value)
                          }
                        >
                          <option value="IN">IN</option>
                          <option value="OUT">OUT</option>
                          <option value="BTIN">BTIN</option>
                          <option value="BTOUT">BTOUT</option>
                          <option value="EXP">EXP</option>
                        </select>
                      ) : (
                        <strong className={tx.paymentType}>
                          {tx.paymentType}
                        </strong>
                      )}
                    </td>

                    {/* 2. From Branch Edit */}
                    <td>
                      {isEditing ? (
                        <input
                          value={editValues.fromBranch}
                          onChange={(e) =>
                            handleInputChange("fromBranch", e.target.value)
                          }
                        />
                      ) : (
                        tx.fromBranch
                      )}
                    </td>

                    {/* 3. To Branch Edit */}
                    <td>
                      {isEditing ? (
                        <input
                          value={editValues.toBranch}
                          onChange={(e) =>
                            handleInputChange("toBranch", e.target.value)
                          }
                        />
                      ) : (
                        tx.toBranch
                      )}
                    </td>

                    {/* 5. Receiver Name Edit */}
                    <td>
                      {isEditing ? (
                        <input
                          value={editValues.receiverName}
                          onChange={(e) =>
                            handleInputChange("receiverName", e.target.value)
                          }
                        />
                      ) : (
                        tx.receiverName
                      )}
                    </td>

                    {/* 6. Status Edit */}
                    <td>
                      {isEditing ? (
                        <select
                          value={editValues.status}
                          onChange={(e) =>
                            handleInputChange("status", e.target.value)
                          }
                        >
                          <option value="DELIVERED">DELIVERED</option>
                          <option value="APPROVED">APPROVED</option>
                          <option value="PENDING">PENDING</option>
                        </select>
                      ) : (
                        tx.status
                      )}
                    </td>

                    {/* 7. Amount Edit */}
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues.amount}
                          onChange={(e) =>
                            handleInputChange("amount", e.target.value)
                          }
                        />
                      ) : (
                        `₹${tx.amount.toLocaleString()}`
                      )}
                    </td>

                    {/* 8. Commission Edit */}
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editValues.commission}
                          onChange={(e) =>
                            handleInputChange("commission", e.target.value)
                          }
                        />
                      ) : (
                        `₹${tx.commission || 0}`
                      )}
                    </td>

                    {/* Actions */}
                    <td className="br-actions">
                      {isEditing ? (
                        <div className="action-save-cancel">
                          <FaSave
                            onClick={() => handleEditSave(tx)}
                            className="icon-save"
                          />
                          <FaTimes
                            onClick={() => setEditingId(null)}
                            className="icon-cancel"
                          />
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: "10px" }}>
                          <FaEdit
                            onClick={() => {
                              setEditingId(tx.paymentId);
                              setEditValues({ ...tx });
                            }}
                            className="icon-edit"
                          />
                          <FaTrash
                            onClick={() => deleteTransaction(tx)}
                            style={{ color: "#ef4444", cursor: "pointer" }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="date-column">
                      <span className="tx-id-short">
                        {new Date(tx.timestamp).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                      <small className="tx-time-short">
                        {new Date(tx.timestamp).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </td>
                    {/* Payment ID (Typically stay read-only as it is the unique key) */}
                    <td className="id-column" title={tx.paymentId}>
                      {tx.paymentId}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* --- PAGINATION --- */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="btn-pagination"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="btn-pagination"
            >
              Next
            </button>
          </div>
        )}
      </main>
      {/* <Message /> */}
    </div>
    </>
  );
}
